import React, { useState, useRef, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MapPin, Link2, Link2Off, Maximize2, Minimize2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const US_CENTER = [39.8, -98.5];
const DEFAULT_ZOOM = 4;

// Color scale based on competitiveness score
const getMarkerColor = (score) => {
  if (score >= 70) return '#229644';
  if (score >= 50) return '#FFA500';
  return '#FF521C';
};

const getMarkerSize = (value) => {
  if (value >= 1000000) return 12;
  if (value >= 500000) return 10;
  if (value >= 200000) return 8;
  return 6;
};

// Sync component to link map movements
function MapSync({ syncEnabled, otherMapRef, mapSide }) {
  const map = useMap();
  
  useMapEvents({
    moveend: () => {
      if (syncEnabled && otherMapRef.current) {
        const center = map.getCenter();
        const zoom = map.getZoom();
        otherMapRef.current.setView(center, zoom, { animate: false });
      }
    },
    zoomend: () => {
      if (syncEnabled && otherMapRef.current) {
        const center = map.getCenter();
        const zoom = map.getZoom();
        otherMapRef.current.setView(center, zoom, { animate: false });
      }
    }
  });

  return null;
}

// Individual map panel component
function MapPanel({ 
  title, 
  data, 
  isLoading, 
  filterMonth, 
  filterRegion,
  onFilterChange,
  mapRef,
  otherMapRef,
  syncEnabled,
  onViewDetails,
  side
}) {
  // Filter data based on selections
  const filteredFeatures = React.useMemo(() => {
    if (!data?.features) return [];
    
    let features = data.features;
    
    if (filterRegion && filterRegion !== 'all') {
      features = features.filter(f => f.properties.isoRegion === filterRegion);
    }
    
    return features;
  }, [data, filterRegion]);

  const stats = React.useMemo(() => {
    return filteredFeatures.reduce((acc, f) => {
      acc.totalValue += f.properties.annualValue;
      acc.count += 1;
      if (f.properties.competitivenessScore < 50) acc.atRisk += 1;
      return acc;
    }, { totalValue: 0, count: 0, atRisk: 0 });
  }, [filteredFeatures]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="p-3 bg-white border-b">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">{title}</h3>
          <Badge variant="outline" className="text-xs">
            {stats.count} contracts
          </Badge>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2">
          <Select value={filterRegion} onValueChange={(v) => onFilterChange('region', v)}>
            <SelectTrigger className="h-8 text-xs flex-1">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="PJM">PJM</SelectItem>
              <SelectItem value="ERCOT">ERCOT</SelectItem>
              <SelectItem value="CAISO">CAISO</SelectItem>
              <SelectItem value="NYISO">NYISO</SelectItem>
              <SelectItem value="ISONE">ISONE</SelectItem>
              <SelectItem value="MISO">MISO</SelectItem>
              <SelectItem value="SPP">SPP</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-4 mt-2 text-xs">
          <span className="text-muted-foreground">
            Value: <span className="font-medium text-[#229644]">{formatCurrency(stats.totalValue)}</span>
          </span>
          <span className="text-muted-foreground">
            At Risk: <span className="font-medium text-red-600">{stats.atRisk}</span>
          </span>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={US_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapSync 
            syncEnabled={syncEnabled} 
            otherMapRef={otherMapRef}
            mapSide={side}
          />
          
          {filteredFeatures.map((feature, idx) => {
            const [lng, lat] = feature.geometry.coordinates;
            const props = feature.properties;
            const color = getMarkerColor(props.competitivenessScore);
            const size = getMarkerSize(props.annualValue);
            
            return (
              <CircleMarker
                key={props.contractId || idx}
                center={[lat, lng]}
                radius={size}
                pathOptions={{
                  color: color,
                  fillColor: color,
                  fillOpacity: 0.7,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="min-w-[180px] text-sm">
                    <div className="font-semibold">{props.accountName}</div>
                    <div className="text-xs text-muted-foreground mb-2">{props.contractNumber}</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>Value:</span>
                        <span className="font-medium">{formatCurrency(props.annualValue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Health:</span>
                        <Badge className={`text-[10px] ${props.competitivenessScore >= 70 ? 'bg-green-100 text-green-800' : props.competitivenessScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                          {props.competitivenessScore}%
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Region:</span>
                        <span>{props.isoRegion || 'N/A'}</span>
                      </div>
                    </div>
                    {onViewDetails && (
                      <Button 
                        size="sm" 
                        className="w-full mt-2 h-7 text-xs"
                        onClick={() => onViewDetails(props.contractId)}
                      >
                        View Details
                      </Button>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

export default function SplitScreenMap({ onViewDetails, onClose }) {
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [leftRegion, setLeftRegion] = useState('all');
  const [rightRegion, setRightRegion] = useState('all');
  
  const leftMapRef = useRef(null);
  const rightMapRef = useRef(null);

  const { data: geoData, isLoading } = useQuery({
    queryKey: ['geo-contracts'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const handleLeftFilterChange = (type, value) => {
    if (type === 'region') setLeftRegion(value);
  };

  const handleRightFilterChange = (type, value) => {
    if (type === 'region') setRightRegion(value);
  };

  return (
    <div className="flex flex-col h-full" data-testid="split-screen-map">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-white border-b">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#09409C]" />
          <h2 className="font-semibold">Split-Screen Comparison</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={syncEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => setSyncEnabled(!syncEnabled)}
            className="h-8"
          >
            {syncEnabled ? (
              <>
                <Link2 className="w-4 h-4 mr-1" />
                Synced
              </>
            ) : (
              <>
                <Link2Off className="w-4 h-4 mr-1" />
                Independent
              </>
            )}
          </Button>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8">
              ✕
            </Button>
          )}
        </div>
      </div>

      {/* Split Maps */}
      <div className="flex-1 flex">
        {/* Left Map */}
        <div className="flex-1 border-r">
          <MapPanel
            title="View A"
            data={geoData}
            isLoading={isLoading}
            filterRegion={leftRegion}
            onFilterChange={handleLeftFilterChange}
            mapRef={leftMapRef}
            otherMapRef={rightMapRef}
            syncEnabled={syncEnabled}
            onViewDetails={onViewDetails}
            side="left"
          />
        </div>
        
        {/* Right Map */}
        <div className="flex-1">
          <MapPanel
            title="View B"
            data={geoData}
            isLoading={isLoading}
            filterRegion={rightRegion}
            onFilterChange={handleRightFilterChange}
            mapRef={rightMapRef}
            otherMapRef={leftMapRef}
            syncEnabled={syncEnabled}
            onViewDetails={onViewDetails}
            side="right"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="p-2 bg-white border-t flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#229644]" />
          <span>Healthy (70%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FFA500]" />
          <span>At Risk (50-69%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FF521C]" />
          <span>Critical (&lt;50%)</span>
        </div>
      </div>
    </div>
  );
}
